import styled from "styled-components";
import { UserIcon } from "lucide-react";
import { monolithMoment } from "../../../utils/date-format";
import { Version } from "../note-history.types";

interface CardItemProps {
  className?: string;
  version: Version;
  userName?: string;
  versionNumber: number;
  onClick?: (version: Version) => void;
  isSelected?: boolean;
}

export const CardItem = ({
  className,
  version,
  userName,
  versionNumber,
  onClick,
  isSelected,
}: CardItemProps) => {
  return (
    <CardContainer
      className={className}
      onClick={() => onClick?.(version)}
      isSelected={isSelected}
    >
      {versionNumber > 0 && <VersionNumber>#{versionNumber}</VersionNumber>}
      <Timestamp isSelected={isSelected}>
        {monolithMoment({
          timestamp: version.created_on,
          format: "MMMM DD, YYYY HH:mm:ss",
        })}
      </Timestamp>
      <UserInfo>
        <UserIcon size={12} />
        <UserName>{userName || "Unknown"}</UserName>
      </UserInfo>
      <VersionId>
        ID: <UuidText>{version.uuid}</UuidText>
      </VersionId>
    </CardContainer>
  );
};

const CardContainer = styled.div<{ isSelected?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 8px 16px;
  margin-bottom: 4px;
  border-radius: 8px;
  background-color: ${({ theme, isSelected }) =>
    isSelected ? `${theme.palette.primary.main}10` : "transparent"};
  border: 1px solid
    ${({ theme, isSelected }) =>
      isSelected
        ? `${theme.palette.primary.main}60`
        : `${theme.palette.divider}`};

  transition: all 0.2s ease-in-out;

  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.palette.action.hover};
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const VersionNumber = styled.div`
  position: absolute;
  bottom: 8px;
  right: 16px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const Timestamp = styled.div<{ isSelected?: boolean }>`
  font-weight: ${({ isSelected }) => (isSelected ? 700 : 550)};
  font-size: 12px;
  color: ${({ theme, isSelected }) =>
    isSelected ? theme.palette.text.primary : theme.palette.text.secondary};
  margin-bottom: 4px;
  line-height: 1.2;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 3px;

  svg {
    color: ${({ theme }) => theme.palette.primary.main};
    flex-shrink: 0;
  }
`;

const UserName = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.palette.text.secondary};
  font-weight: 400;
`;

const VersionId = styled.div`
  font-size: 10px;
  opacity: 0.7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const UuidText = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.palette.text.secondary};
  font-family: monospace;
`;
